/**
 * *Add an Asset* at the table's foot (`equipment-page-table`, R17–R18; frame 07's foot line).
 *
 * A Name field with the twin's *-1, -2* answer under it, as in a cell. Enter on a name that can be
 * saved adds the asset, empties the field and leaves it open for the next one. Escape, or leaving
 * it empty, closes it.
 *
 * **One idempotency key per add attempt.** The key is minted for the name being added and reused
 * when Enter is pressed again on the same name after an outcome nobody could confirm (a timeout, a
 * dropped connection), so the server answers the first create rather than making a second. A new
 * name — or a confirmed answer — starts a new attempt with a new key.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Input } from '@repo/design-system';
import { space } from '@repo/design-tokens';
import { nameKey, type TwinNameIndex } from '@repo/front-end-utils';
import type { TwinAssetResponse } from '@repo/front-end-services';

import { nameAnswer, type NameAnswer } from './equipmentRowEdit';
import { newAddAttemptKey, type AddOutcome } from './useEquipmentRowWrites';
import { AnswerLine } from './EditableRegisterCell';

/** An add being retried: what was typed, the answer sent, its key, and whether it is unresolved. */
export type AddAttempt = { typed: string; answer: NameAnswer; key: string; retry: boolean };

export interface EquipmentAddRowProps {
  /** The twin's names, every register row and plan entity included. */
  nameIndex: TwinNameIndex;
  onAdd: (answer: NameAnswer, idempotencyKey: string) => Promise<AddOutcome>;
  onAdded: (created: TwinAssetResponse) => void;
  /** `cancelled`: Escape — focus goes back to *Add an Asset*. Otherwise the field was left empty. */
  onClose: (cancelled: boolean) => void;
  /**
   * Where the attempt is kept. The host's, so an unresolved attempt survives this row unmounting —
   * a failed re-read hides the table and the add row with it — and its key is not lost (Copilot,
   * PR #2157). Absent, the row keeps its own.
   */
  attemptRef?: React.MutableRefObject<AddAttempt | null>;
}

const ANSWER_ID = 'equipment-add-answer';

const Row = styled.div`
  display: flex;
  flex-direction: column;
  max-width: calc(${space[12]} * 7);
`;

const Line = styled.span`
  display: flex;
  margin-top: ${space[1]};
  min-width: 0;
`;

export const EquipmentAddRow: React.FC<EquipmentAddRowProps> = ({
  nameIndex,
  onAdd,
  onAdded,
  onClose,
  attemptRef,
}) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // *A name is needed.* is said once Enter is pressed on an empty field, not on opening it.
  const [triedEmpty, setTriedEmpty] = useState(false);
  /*
   * The attempt being retried. On an outcome nobody could confirm, Enter again on the SAME typed
   * text re-sends this attempt exactly — its name and its key, and no holder rename, which already
   * landed — so the server replays the first create. Recomputing the answer instead would see the
   * first create (the re-read shows it), answer a new name, mint a new key and make a second asset.
   */
  const ownAttempt = useRef<AddAttempt | null>(null);
  const attempt = attemptRef ?? ownAttempt;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const answer = nameAnswer(typed, null, nameIndex);
  const line = failure ?? (answer.kind === 'empty' && !triedEmpty ? null : answer.line);
  const refused = failure !== null || answer.kind === 'empty';

  const submit = async () => {
    if (busy) return;
    if (answer.kind === 'empty') {
      setTriedEmpty(true);
      return;
    }
    const previous = attempt.current;
    const retrying = previous !== null && previous.retry && previous.typed === nameKey(typed);
    const sending: NameAnswer = retrying
      ? { kind: 'free', name: previous.answer.name, line: null }
      : answer;
    const key = retrying ? previous.key : newAddAttemptKey();
    attempt.current = { typed: nameKey(typed), answer: sending, key, retry: false };
    setBusy(true);
    const outcome = await onAdd(sending, key);
    setBusy(false);
    if (outcome.ok) {
      attempt.current = null;
      setTyped('');
      setFailure(null);
      onAdded(outcome.created);
    } else {
      // Still unresolved after a definite failure of its retry (a token that would not come, say):
      // the first POST may have landed, so the key is kept until a success or a new name.
      attempt.current = {
        typed: nameKey(typed),
        answer: sending,
        key,
        retry: outcome.retry || retrying,
      };
      setFailure(outcome.line);
    }
    // After an add, focus stays in the field (A2) — unless the person has moved on meanwhile:
    // taking focus back would blur the field they are in, and blur commits (Copilot, PR #2157).
    const active = document.activeElement;
    if (!active || active === document.body || active === inputRef.current) {
      inputRef.current?.focus();
    }
  };

  return (
    <Row data-testid="equipment-add-row">
      <Input
        ref={inputRef}
        size="compact"
        onGlass
        value={typed}
        // Read-only rather than disabled while saving, so focus stays in the field.
        readOnly={busy}
        aria-busy={busy || undefined}
        invalid={failure !== null}
        placeholder="Name"
        aria-label="Name of the new asset"
        aria-describedby={line ? ANSWER_ID : undefined}
        onChange={event => {
          setTyped(event.target.value);
          setFailure(null);
          setTriedEmpty(false);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void submit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onClose(true);
          }
        }}
        onBlur={() => {
          if (!busy && typed.trim() === '') onClose(false);
        }}
      />
      {(busy || line) && (
        <Line>
          <AnswerLine
            id={ANSWER_ID}
            $refused={!busy && refused && answer.kind !== 'free'}
            title={busy ? undefined : (line ?? undefined)}
          >
            {busy ? 'Saving…' : line}
          </AnswerLine>
        </Line>
      )}
    </Row>
  );
};

export default EquipmentAddRow;
